import { DatasetInfo, Photo } from 'Compass/profile-lab/types';
import { DEFAULT_MODEL } from 'Compass/profile-lab/utils/llmConstants';

const DB_NAME = 'profile-lab-dazzle-photos';
const DB_VERSION = 9;

export interface SelectionState {
  id: number;
  selected: number[];
  grow: number[];
  lastModified: number;
}

export interface ChatMessage {
  id?: number;
  content: string;
  sender: 'user' | 'assistant' | 'system';
  timestamp: number;
}

export interface ChatConfig {
  id: number;
  modelType: string;
  sendHistory: boolean;
  mode: 'adhoc' | 'stage';
  lastModified: number;
}

export interface WorkingStage {
  id: number;
  stageId: string;
  template: string | null;
  questions: string | null;
  combinerTemplate: string | null;
  datasets: string[];
  chunkSize: number;
  templateOnly: boolean;
  modelType: string | null;
  photoSelectionConfig: {
    maxMonths: number | null;
    limit: number | null;
    onlySelfIdentity: boolean;
    hasFaces: boolean;
  } | null;
  lastModified: number;
}

export interface SyncMetadata {
  id: number;
  lastSyncTimestamp: number;
}

export interface SubjectConfig {
  id: number;
  userLid: string;
  lastModified: number;
}

export interface DatasetState {
  id: number;
  datasets: DatasetInfo[];
  lastModified: number;
}

class DatabaseService {
  private db: IDBDatabase | null = null;
  private initPromise: Promise<void> | null = null;

  async init(): Promise<void> {
    if (this.db) return;
    if (this.initPromise) return this.initPromise;

    this.initPromise = new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        this.db = request.result;
        resolve();
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        const oldVersion = event.oldVersion;

        if (oldVersion < 5 && db.objectStoreNames.contains('assets')) {
          db.deleteObjectStore('assets');
        }

        if (!db.objectStoreNames.contains('photos')) {
          const photoStore = db.createObjectStore('photos', { keyPath: 'photo_lid' });
          photoStore.createIndex('photo_date', 'photo_date', { unique: false });
        }

        if (!db.objectStoreNames.contains('selections')) {
          db.createObjectStore('selections', { keyPath: 'id' });
        }

        if (!db.objectStoreNames.contains('chat_history')) {
          const chatStore = db.createObjectStore('chat_history', { keyPath: 'id', autoIncrement: true });
          chatStore.createIndex('timestamp', 'timestamp', { unique: false });
        }

        if (!db.objectStoreNames.contains('chat_ui_messages')) {
          const chatUIStore = db.createObjectStore('chat_ui_messages', { keyPath: 'id', autoIncrement: true });
          chatUIStore.createIndex('timestamp', 'timestamp', { unique: false });
        }

        if (!db.objectStoreNames.contains('chat_config')) {
          db.createObjectStore('chat_config', { keyPath: 'id' });
        }

        if (!db.objectStoreNames.contains('sync_metadata')) {
          db.createObjectStore('sync_metadata', { keyPath: 'id' });
        }

        if (!db.objectStoreNames.contains('subject_config')) {
          db.createObjectStore('subject_config', { keyPath: 'id' });
        }

        if (!db.objectStoreNames.contains('datasets')) {
          db.createObjectStore('datasets', { keyPath: 'id' });
        }

        if (!db.objectStoreNames.contains('working_stage')) {
          db.createObjectStore('working_stage', { keyPath: 'id' });
        }
      };
    });

    return this.initPromise;
  }

  private async getDB(): Promise<IDBDatabase> {
    if (!this.db) {
      await this.init();
    }
    if (!this.db) throw new Error('Database not initialized');
    return this.db;
  }

  async addPhoto(photo: Photo): Promise<void> {
    const db = await this.getDB();
    const tx = db.transaction('photos', 'readwrite');
    const store = tx.objectStore('photos');
    return new Promise((resolve, reject) => {
      const request = store.put(photo);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  async addPhotosBulk(photos: Photo[]): Promise<void> {
    const db = await this.getDB();
    const tx = db.transaction('photos', 'readwrite');
    const store = tx.objectStore('photos');

    for (const photo of photos) {
      store.put(photo);
    }

    return new Promise((resolve, reject) => {
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  }

  async getPhoto(zPk: number): Promise<Photo | undefined> {
    const db = await this.getDB();
    const tx = db.transaction('photos', 'readonly');
    return new Promise((resolve, reject) => {
      const request = tx.objectStore('photos').get(zPk);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async getAllPhotos(): Promise<Photo[]> {
    const db = await this.getDB();
    const tx = db.transaction('photos', 'readonly');
    return new Promise<Photo[]>((resolve, reject) => {
      const request = tx.objectStore('photos').getAll();
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async getPhotosCount(): Promise<number> {
    const db = await this.getDB();
    const tx = db.transaction('photos', 'readonly');
    return new Promise((resolve, reject) => {
      const request = tx.objectStore('photos').count();
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async clearPhotos(): Promise<void> {
    const db = await this.getDB();
    const tx = db.transaction('photos', 'readwrite');
    return new Promise((resolve, reject) => {
      const request = tx.objectStore('photos').clear();
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  async getSelectionState(): Promise<SelectionState> {
    const db = await this.getDB();
    const tx = db.transaction('selections', 'readonly');
    const state = await new Promise<SelectionState | undefined>((resolve, reject) => {
      const request = tx.objectStore('selections').get(1);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });

    if (!state) {
      return {
        id: 1,
        selected: [],
        grow: [],
        lastModified: Date.now()
      };
    }

    return state;
  }

  async setSelectionState(state: Omit<SelectionState, 'id' | 'lastModified'>): Promise<void> {
    const db = await this.getDB();
    const tx = db.transaction('selections', 'readwrite');
    return new Promise((resolve, reject) => {
      const request = tx.objectStore('selections').put({
        id: 1,
        ...state,
        lastModified: Date.now()
      });
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  async updateSelection(updates: Partial<Pick<SelectionState, 'selected' | 'grow'>>): Promise<void> {
    const current = await this.getSelectionState();
    const db = await this.getDB();
    const tx = db.transaction('selections', 'readwrite');

    return new Promise((resolve, reject) => {
      const request = tx.objectStore('selections').put({
        ...current,
        ...updates,
        lastModified: Date.now()
      });
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  async addChatMessage(message: Omit<ChatMessage, 'id'>): Promise<number> {
    const db = await this.getDB();
    const tx = db.transaction('chat_history', 'readwrite');
    return new Promise((resolve, reject) => {
      const request = tx.objectStore('chat_history').add(message);
      request.onsuccess = () => resolve(request.result as number);
      request.onerror = () => reject(request.error);
    });
  }

  async getChatHistory(): Promise<ChatMessage[]> {
    const db = await this.getDB();
    const tx = db.transaction('chat_history', 'readonly');
    return new Promise((resolve, reject) => {
      const request = tx.objectStore('chat_history').getAll();
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async clearChatHistory(): Promise<void> {
    const db = await this.getDB();
    const tx = db.transaction('chat_history', 'readwrite');
    return new Promise((resolve, reject) => {
      const request = tx.objectStore('chat_history').clear();
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  async addUIMessage(message: Omit<ChatMessage, 'id'>): Promise<number> {
    const db = await this.getDB();
    const tx = db.transaction('chat_ui_messages', 'readwrite');
    return new Promise((resolve, reject) => {
      const request = tx.objectStore('chat_ui_messages').add(message);
      request.onsuccess = () => resolve(request.result as number);
      request.onerror = () => reject(request.error);
    });
  }

  async getUIMessages(): Promise<ChatMessage[]> {
    const db = await this.getDB();
    const tx = db.transaction('chat_ui_messages', 'readonly');
    return new Promise((resolve, reject) => {
      const request = tx.objectStore('chat_ui_messages').getAll();
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async clearUIMessages(): Promise<void> {
    const db = await this.getDB();
    const tx = db.transaction('chat_ui_messages', 'readwrite');
    return new Promise((resolve, reject) => {
      const request = tx.objectStore('chat_ui_messages').clear();
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  async getChatConfig(): Promise<ChatConfig> {
    const db = await this.getDB();
    const tx = db.transaction('chat_config', 'readonly');
    const config = await new Promise<ChatConfig | undefined>((resolve, reject) => {
      const request = tx.objectStore('chat_config').get(1);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });

    if (!config) {
      return {
        id: 1,
        modelType: DEFAULT_MODEL,
        sendHistory: false,
        mode: 'adhoc',
        lastModified: Date.now()
      };
    }

    const migrated: ChatConfig = {
      id: config.id,
      modelType: config.modelType || DEFAULT_MODEL,
      sendHistory: config.sendHistory,
      mode: config.mode || 'adhoc',
      lastModified: config.lastModified,
    };

    return migrated;
  }

  async setChatConfig(config: Omit<ChatConfig, 'id' | 'lastModified'>): Promise<void> {
    const db = await this.getDB();
    const tx = db.transaction('chat_config', 'readwrite');
    return new Promise((resolve, reject) => {
      const request = tx.objectStore('chat_config').put({
        id: 1,
        ...config,
        lastModified: Date.now()
      });
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  async updateChatConfig(updates: Partial<Omit<ChatConfig, 'id' | 'lastModified'>>): Promise<void> {
    const current = await this.getChatConfig();
    const db = await this.getDB();
    const tx = db.transaction('chat_config', 'readwrite');

    return new Promise((resolve, reject) => {
      const request = tx.objectStore('chat_config').put({
        ...current,
        ...updates,
        lastModified: Date.now()
      });
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }


  generateHash(photoIds: number[]): string {
    const sorted = [...photoIds].sort((a, b) => a - b);
    const hashInput = sorted.join(',');

    let hash = 0;
    for (let i = 0; i < hashInput.length; i++) {
      const char = hashInput.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash;
    }

    return Math.abs(hash).toString(16).padStart(16, '0').substring(0, 16);
  }

  async generateHashAsync(photoIds: number[]): Promise<string> {
    const hashInput = photoIds.join(',');
    const encoder = new TextEncoder();
    const data = encoder.encode(hashInput);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
    return hashHex.substring(0, 16);
  }

  async getSyncMetadata(): Promise<SyncMetadata> {
    const db = await this.getDB();
    const tx = db.transaction('sync_metadata', 'readonly');
    const metadata = await new Promise<SyncMetadata | undefined>((resolve, reject) => {
      const request = tx.objectStore('sync_metadata').get(1);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });

    if (!metadata) {
      return {
        id: 1,
        lastSyncTimestamp: 0
      };
    }

    return metadata;
  }

  async setSyncMetadata(timestamp: number): Promise<void> {
    const db = await this.getDB();
    const tx = db.transaction('sync_metadata', 'readwrite');
    return new Promise((resolve, reject) => {
      const request = tx.objectStore('sync_metadata').put({
        id: 1,
        lastSyncTimestamp: timestamp
      });
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  async getSubjectConfig(): Promise<SubjectConfig> {
    const db = await this.getDB();
    const tx = db.transaction('subject_config', 'readonly');
    const config = await new Promise<SubjectConfig | undefined>((resolve, reject) => {
      const request = tx.objectStore('subject_config').get(1);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });

    if (!config) {
      return {
        id: 1,
        userLid: '',
        lastModified: Date.now()
      };
    }

    return config;
  }

  async setSubjectConfig(userLid: string): Promise<void> {
    const db = await this.getDB();
    const tx = db.transaction('subject_config', 'readwrite');
    return new Promise((resolve, reject) => {
      const request = tx.objectStore('subject_config').put({
        id: 1,
        userLid,
        lastModified: Date.now()
      });
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  async getDBStats(): Promise<{
    photos: number;
    chatHistory: number;
    selections: SelectionState;
    config: ChatConfig;
    syncMetadata: SyncMetadata;
    subjectConfig: SubjectConfig;
  }> {
    const db = await this.getDB();

    const photosCount = await new Promise<number>((resolve, reject) => {
      const tx = db.transaction('photos', 'readonly');
      const request = tx.objectStore('photos').count();
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });

    const chatHistoryCount = await new Promise<number>((resolve, reject) => {
      const tx = db.transaction('chat_history', 'readonly');
      const request = tx.objectStore('chat_history').count();
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });

    const selections = await this.getSelectionState();
    const config = await this.getChatConfig();
    const syncMetadata = await this.getSyncMetadata();
    const subjectConfig = await this.getSubjectConfig();

    return {
      photos: photosCount,
      chatHistory: chatHistoryCount,
      selections,
      config,
      syncMetadata,
      subjectConfig
    };
  }

  async resetDB(): Promise<void> {
    if (this.db) {
      this.db.close();
      this.db = null;
    }
    this.initPromise = null;

    await new Promise<void>((resolve, reject) => {
      const request = indexedDB.deleteDatabase(DB_NAME);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
      request.onblocked = () => {
        console.warn('Database deletion blocked, retrying...');
      };
    });

    await this.init();
  }

  async getDatasetState(): Promise<DatasetState> {
    const db = await this.getDB();
    let tx = db.transaction('datasets', 'readonly');
    const state = await new Promise<DatasetState | undefined>((resolve, reject) => {
      const request = tx.objectStore('datasets').get(1);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });

    const defaultDatasets = [
      { name: 'visits', enabled: false },
      { name: 'profile', enabled: false }
    ];

    if (!state) {
      return {
        id: 1,
        datasets: defaultDatasets,
        lastModified: Date.now()
      };
    }

    let needsMigration = false;
    const migratedDatasets = [...state.datasets];

    for (const defaultDataset of defaultDatasets) {
      if (!migratedDatasets.find(d => d.name === defaultDataset.name)) {
        migratedDatasets.push(defaultDataset);
        needsMigration = true;
      }
    }

    if (needsMigration) {
      const migratedState = {
        ...state,
        datasets: migratedDatasets,
        lastModified: Date.now()
      };

      tx = db.transaction('datasets', 'readwrite');
      await new Promise<void>((resolve, reject) => {
        const request = tx.objectStore('datasets').put(migratedState);
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      });

      return migratedState;
    }

    return state;
  }

  async updateDatasetState(updates: Partial<Omit<DatasetState, 'id' | 'lastModified'>>): Promise<void> {
    const current = await this.getDatasetState();
    const db = await this.getDB();
    const tx = db.transaction('datasets', 'readwrite');

    return new Promise((resolve, reject) => {
      const request = tx.objectStore('datasets').put({
        ...current,
        ...updates,
        lastModified: Date.now()
      });
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  async getDatasetByName(name: string): Promise<DatasetInfo | undefined> {
    const state = await this.getDatasetState();
    return state.datasets.find(d => d.name === name);
  }

  async setDataset(dataset: DatasetInfo): Promise<void> {
    const state = await this.getDatasetState();
    const existingIndex = state.datasets.findIndex(d => d.name === dataset.name);

    if (existingIndex >= 0) {
      state.datasets[existingIndex] = dataset;
    } else {
      state.datasets.push(dataset);
    }

    await this.updateDatasetState({ datasets: state.datasets });
  }

  async removeDataset(name: string): Promise<boolean> {
    const state = await this.getDatasetState();
    const existingIndex = state.datasets.findIndex(d => d.name === name);

    if (existingIndex >= 0) {
      state.datasets.splice(existingIndex, 1);
      await this.updateDatasetState({ datasets: state.datasets });
      return true;
    }

    return false;
  }

  async getWorkingStage(): Promise<WorkingStage | null> {
    const db = await this.getDB();
    const tx = db.transaction('working_stage', 'readonly');
    const stage = await new Promise<WorkingStage | undefined>((resolve, reject) => {
      const request = tx.objectStore('working_stage').get(1);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });

    if (!stage) return null;

    const migratedStage: WorkingStage = {
      ...stage,
      datasets: stage.datasets || [],
      chunkSize: stage.chunkSize || 200,
      templateOnly: stage.templateOnly || false,
      modelType: stage.modelType || null,
      photoSelectionConfig: stage.photoSelectionConfig || null,
    };

    if (stage.datasets === undefined || stage.chunkSize === undefined || stage.modelType === undefined) {
      await this.setWorkingStage(migratedStage);
    }

    return migratedStage;
  }

  async setWorkingStage(stage: Omit<WorkingStage, 'id' | 'lastModified'>): Promise<void> {
    const db = await this.getDB();
    const tx = db.transaction('working_stage', 'readwrite');
    return new Promise((resolve, reject) => {
      const request = tx.objectStore('working_stage').put({
        id: 1,
        ...stage,
        lastModified: Date.now()
      });
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  async clearWorkingStage(): Promise<void> {
    const db = await this.getDB();
    const tx = db.transaction('working_stage', 'readwrite');
    return new Promise((resolve, reject) => {
      const request = tx.objectStore('working_stage').delete(1);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }
}

export const db = new DatabaseService();
